import { LOCALES } from '@sjuni/types';
import { cn, Textarea } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';

import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';

/**
 * A translatable field holding a *list* — `partners`, `required_documents`.
 *
 * One item per line, in a textarea. Not a row of inputs with add and remove
 * buttons: the content is a handful of short strings that an editor most often
 * has already, in a list, in an email — and a textarea lets them paste it. Six
 * click-to-add rows would turn one paste into six.
 *
 * The value is the same per-locale map every translatable field uses, except
 * that each locale holds an array. Blank lines are dropped on the way out, so
 * trailing newlines and double spacing do not become empty bullets on the
 * public page.
 */
export interface TranslatableListFieldProps {
  label: string;
  /** `{ka: string[], en: string[]}` — the shape `translatedArray()` returns. */
  value: Record<string, string[] | null>;
  onChange: (next: Record<string, string[] | null>) => void;
  hint?: string;
  error?: string;
  rows?: number;
  disabled?: boolean;
}

export function TranslatableListField({
  label,
  value,
  onChange,
  hint,
  error,
  rows = 4,
  disabled,
}: TranslatableListFieldProps) {
  const { t } = useTranslation();
  const { locale, setLocale } = useEditorLocale();

  const items = itemsFor(value, locale);

  return (
    <div>
      <div className="mb-1 flex items-end justify-between gap-3">
        <div
          role="radiogroup"
          aria-label={t('form.localeFor', { field: label })}
          className="ms-auto flex items-center gap-1"
        >
          {LOCALES.map((candidate) => {
            const filled = itemsFor(value, candidate).length > 0;

            return (
              <button
                key={candidate}
                type="button"
                role="radio"
                aria-checked={locale === candidate}
                onClick={() => setLocale(candidate)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-caption font-extrabold uppercase transition-colors duration-fast',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
                  locale === candidate
                    ? 'bg-navy-600 text-white'
                    : 'bg-surface-alt text-secondary hover:text-primary',
                )}
              >
                {candidate}
                <span
                  aria-hidden="true"
                  className={cn(
                    'ms-1 inline-block h-1.5 w-1.5 rounded-full align-middle',
                    filled ? 'bg-success' : 'bg-current opacity-40',
                  )}
                />
                <span className="sr-only">
                  {filled ? t('form.localeFilled') : t('form.localeEmpty')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Textarea
        label={label}
        hint={hint ?? t('form.list.hint')}
        error={error}
        rows={rows}
        disabled={disabled}
        value={items.join('\n')}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange({
            ...value,
            /*
             * Split without filtering, so a line an editor is part-way through
             * typing does not vanish under them. The blanks are dropped on
             * save instead — see `listToInput`.
             */
            [locale]: event.target.value.split('\n'),
          })
        }
      />

      {items.filter((item) => item.trim() !== '').length > 0 ? (
        <p className="mt-1.5 text-caption text-muted">
          {t('form.list.count', { count: items.filter((item) => item.trim() !== '').length })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The stored value for one locale, whatever shape it turns out to be.
 *
 * It should always be an array — `translatableListRules()` validates it as one
 * and `translatedArray()` reads it back as one — but the database has been
 * through a migration and a seeder, and a field that assumed the shape blanked
 * the whole programme editor with `items.join is not a function` when it met a
 * string. A form is not the place to discover that a column was mis-specified;
 * it degrades and the editor can see the value.
 *
 * A string becomes its lines, which is the shape somebody would have typed
 * anyway. Anything else becomes nothing.
 */
function itemsFor(value: Record<string, unknown>, locale: string): string[] {
  const stored = value[locale];

  if (Array.isArray(stored)) {
    return stored.map((item) => String(item));
  }

  if (typeof stored === 'string') {
    return stored.split('\n');
  }

  return [];
}
